import { cn } from '@/lib/utils'

/**
 * A person, as a coloured circle with their initials — the same symbol the top bar shows for the
 * signed-in user, for the places that name somebody else.
 *
 * The colour is derived from the name rather than stored or assigned, so the same person is the
 * same colour everywhere, in every list, without anything having to remember it.
 */
export function UserAvatar({
  firstName,
  lastName,
  size = 'sm',
  className,
}: {
  firstName: string
  lastName: string
  size?: 'sm' | 'md'
  className?: string
}) {
  const name = fullName(firstName, lastName)
  const background = colourFromName(name)

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium',
        size === 'md' ? 'h-8 w-8 text-sm' : 'h-6 w-6 text-[10px]',
        className,
      )}
      style={{ backgroundColor: background, color: readableOn(background) }}
      aria-hidden
    >
      {initialsOf(firstName, lastName)}
    </span>
  )
}

export function fullName(firstName: string, lastName: string): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim()
}

/**
 * Initials, from the two names when there are two and from whatever there is when there is not.
 * A user with no name at all still has to draw as something rather than as an empty circle.
 */
export function initialsOf(firstName: string, lastName: string): string {
  const first = firstName.trim()
  const last = lastName.trim()

  if (first && last) return (first[0] + last[0]).toUpperCase()

  const only = first || last
  if (!only) return '?'
  return only.slice(0, 2).toUpperCase()
}

/**
 * A colour for a name: hash the string, then take three bytes of the hash as red, green and blue.
 *
 * Any hash would do. What matters is that it is a pure function of the name, so two places showing
 * the same person agree without coordinating, and that the whole name feeds it, so two people who
 * share a first name are told apart.
 */
export function colourFromName(name: string): string {
  let hash = 0
  for (let index = 0; index < name.length; index += 1) {
    hash = name.charCodeAt(index) + ((hash << 5) - hash)
  }

  let colour = '#'
  for (let byte = 0; byte < 3; byte += 1) {
    const value = (hash >> (byte * 8)) & 0xff
    colour += value.toString(16).padStart(2, '0')
  }
  return colour
}

/**
 * Black or white, whichever can be read on the given colour.
 *
 * The weights are the usual ones for perceived brightness: the eye takes far more of it from green
 * than from blue, so a plain average would call some light greens dark and print white on them.
 */
export function readableOn(colour: string): string {
  const red = parseInt(colour.slice(1, 3), 16)
  const green = parseInt(colour.slice(3, 5), 16)
  const blue = parseInt(colour.slice(5, 7), 16)
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000

  return brightness > 140 ? '#0B2040' : '#ffffff'
}
